import { setupConsoleInterceptor } from './interceptor';
import { ConsoleEvent } from './console';
import { v4 as uuidv4 } from 'uuid';
import { getChromeRuntime } from '../chrome/wrappers';
import { getAccountStore, getLogsStore } from '../storage/stores';
import { uploadFileToS3 } from '../lib/s3/s3PresignedUrl';
import { buildLRUCache } from '../lib/cache/LRUCache';
import { ChromeMessageType } from '../types';
import { fetchUploadsPresignedUrl } from '../api/fetchUploadsPresignedUrl';

// How often to persist logs to the store
const LOG_PERSIST_INTERVAL = 60_000; // 1 minute
// Maximum number of logs to store in the cache
const LOG_MAX_ENTRIES = 10_000;

const logsLRU = buildLRUCache<string, ConsoleEvent>(LOG_MAX_ENTRIES);

/**
 * Sets up the remote logger to capture logs and upload them to S3 when the user action is triggered.
 */
export const setupRemoteLogger = () => {
  // Load previous session logs from store
  logsLRU.init(getLogsStore().get()?.logs || []);

  // Setup console interceptor to capture logs and store them in the LRU cache
  setupConsoleInterceptor([
    (event: ConsoleEvent) => {
      logsLRU.set(`${event.timestamp}-${uuidv4()}`, event);
    },
  ]);

  // Listen for user action to upload logs and cleanup
  listenForUserAction();

  // Persist logs every X seconds
  setInterval(persistLogs, LOG_PERSIST_INTERVAL);
};

/**
 * Uploads the logs to S3 and removes them from the LRU cache.
 *
 * @returns Promise resolving to void.
 * @throws Error if the logs fail to upload.
 */
export const uploadLogsAndCleanup = async () => {
  try {
    // Check if there is an account UID available
    const accountUid = getAccountStore().get()?.accountUid;
    if (!accountUid) {
      throw new Error('No account UID available for log upload');
    }

    // Check if there are any logs to upload
    const numberOfLogs = logsLRU.size();
    if (numberOfLogs === 0) {
      console.warn('No logs to upload');
      return;
    }

    console.log(`Starting upload of ${numberOfLogs} logs to S3`);
    const uploadedLogs = await uploadLogs(accountUid);

    // Only clear uploaded logs if was successful
    for (const key of uploadedLogs) {
      logsLRU.deleteByKey(key);
    }
    console.log('Logs uploaded successfully and cache cleared');
  } catch (error) {
    console.error('Failed to upload logs and cleanup:', error);
    throw error;
  }
};

const uploadLogs = async (accountUid: string): Promise<string[]> => {
  // Get the S3 presigned URL from the API
  const url = (await fetchUploadsPresignedUrl(accountUid)).data.url;

  // Get the logs from the LRU cache and upload them to S3
  const { keys, values } = logsLRU.toKeyValueArrays();
  const logsJson = JSON.stringify(values, null, 2);
  await uploadFileToS3(url, logsJson);

  return keys;
};

const persistLogs = () => getLogsStore().set({ logs: logsLRU.toJSON() });

const listenForUserAction = () => {
  getChromeRuntime().onMessage.addListener((message, _, sendResponse) => {
    if (message.type === ChromeMessageType.UPLOAD_LOGS_AND_CLEANUP) {
      uploadLogsAndCleanup()
        .then(() => {
          sendResponse({ success: true });
        })
        .catch(error => {
          sendResponse({
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        });
      return true; // Keep message channel open for async response
    }
  });
};
